import React, { useState, useRef, useEffect } from "react";
import { useCurrency } from "./currency-context";
import { ChevronDown } from "lucide-react";
import { Currency } from "./types";

interface CurrencyToggleProps {
  className?: string;
  availableCurrencies?: string[];
}

export const CurrencyToggle: React.FC<CurrencyToggleProps> = ({ className, availableCurrencies: availablePlanCurrencies }) => {
  const { currency, setCurrency, availableCurrencies } = useCurrency();
  const [open, setOpen] = useState(false);

  // 🔥 NEW: search state
  const [search, setSearch] = useState("");
  const timeoutRef = useRef<any>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  let currencies = Object.keys(availableCurrencies);

  if (availablePlanCurrencies && availablePlanCurrencies.length > 0) {
    currencies = currencies.filter(cur => availablePlanCurrencies.includes(cur));
  }

  // 🔥 NEW: filter currencies based on typed keys
  const filteredCurrencies = currencies.filter(cur =>
    cur.toLowerCase().startsWith(search.toLowerCase())
  );

  const handleSelect = (value: string) => {
    setCurrency(value as Currency);
    setOpen(false);
    setSearch(""); // reset search
  };

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
        setSearch(""); // reset search
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔥 NEW: keyboard typing logic
useEffect(() => {
  let lastTypeTime = 0;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!open) return;

    if (/^[a-zA-Z]$/.test(e.key)) {
      const now = Date.now();

      setSearch((prev) => {
        // if typing gap > 500ms → start fresh
        if (now - lastTypeTime > 500) {
          return e.key;
        }
        return prev + e.key;
      });

      lastTypeTime = now;
    }

    if (e.key === "Backspace") {
      setSearch((prev) => prev.slice(0, -1));
    }
  };

  window.addEventListener("keydown", handleKeyDown);
  return () => window.removeEventListener("keydown", handleKeyDown);
}, [open]);

  return (
    <div ref={dropdownRef} className={`relative inline-block ${className}`}>
      
      {/* Button */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center space-x-2 px-30 py-18 gap-16 border-2 border-[#0072ff] shadow-lg rounded-full text-white bg-[#0072ff]"
      >
        <span>{currency}</span>

        <ChevronDown
          size={18}
          className={`transition-transform text-white ${open ? "" : "rotate-180"}`}
        />
      </button>

      {/* Dropdown */}
      {open && filteredCurrencies.length > 0 && (
        <div
          className={`absolute mt-4 w-full bg-white text-black text-center border-2 border-[#0072ff] shadow-lg z-10 overflow-hidden transform transition-all duration-300 ease-in-out ${
            open
              ? "opacity-100 scale-y-100"
              : "opacity-0 scale-y-95 pointer-events-none"
          }`}
        >
          {filteredCurrencies.map((cur) => (
            <button
              key={cur}
              onClick={() => handleSelect(cur)}
              className={`block w-full px-4 py-2 hover:bg-gray-100 hover:text-[#0072ff] ${
                currency === cur ? "font-semibold" : ""
              }`}
            >
              <span className="block font-semibold">
                {cur}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};